// What this player has met, across games (§16.2).
//
// The counts come from `mechanics.ts`; this is only where they are kept. The
// split matters because the two halves have opposite properties: the reduction
// is pure and lives forever, and the store is a browser thing that may be
// absent, corrupt, or edited by whoever owns the browser.
//
// **Three rules, and each of them rules something out:**
//
//   - **`src/engine/rules/` may never read this.** Two clients derive state from one
//     log; a rule consulting play history would derive two different games from
//     it and the hash canary would fire with no bug to find.
//   - **It outlives a run.** Game state resets and a profile must not, or a
//     player's second run re-teaches mining. That is the whole point of it.
//   - **It must be forgeable without consequence.** Somebody editing their own
//     `localStorage` to skip the tutorial should change nothing else. If that
//     is not harmless, this has been wired somewhere it should not be — so the
//     test for "have I put this in the wrong place" is to ask whether a lie
//     here could affect anybody else's game.

import {
  MECHANICS_VERSION,
  type MechanicCounts,
  type MechanicId,
  tally
} from "./mechanics";

export interface Profile {
  /** The `MECHANICS_VERSION` these counts were computed under. */
  version: number;
  counts: MechanicCounts;
  /** Games seen, so "is this their first run" is answerable without a count of one. */
  games: number;
  /**
   * Concepts the player has waved away. Per concept and it stays dismissed
   * (§16.4) — a hint dismissed once and offered again is a hint that does not
   * take no for an answer.
   *
   * Optional in the format, like the run save's difficulty: an older profile
   * is a profile with nothing dismissed, which is exactly right, and throwing
   * one away over a field it predates would lose the counts that took ten
   * games to build.
   */
  dismissed?: MechanicId[];
  /** Hints off entirely. Undefined means "decide from the counts". */
  hints?: boolean;
}

export const emptyProfile = (): Profile => ({
  version: MECHANICS_VERSION,
  counts: {},
  games: 0
});

/** Where it is kept. One key, so clearing it is one thing a person can do. */
export const PROFILE_KEY = "gambit.profile.v1";

/**
 * The slice of `localStorage` this needs, so a test needs no browser.
 *
 * Structural rather than importing a DOM type: the whole surface is two
 * methods, and depending on `Storage` would make this module refuse to load
 * under node — which is where its tests run.
 */
export interface ProfileStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const browserStore = (): ProfileStore | null =>
  typeof localStorage === "undefined" ? null : localStorage;

/**
 * Read the profile, or a fresh one.
 *
 * **A version mismatch throws the counts away rather than migrating them.**
 * The counts are a cache of a reduction and the reduction has changed, so what
 * is stored no longer means what it says — and unlike a champion's weights
 * there is nothing here worth carrying forward badly. Backfilling from logs
 * (`mechanicsOfLog`) is the honest way to get them back, and it is available
 * precisely because the reduction is pure.
 *
 * Anything unreadable is treated the same way. This is a browser store that a
 * person is explicitly allowed to edit, so a parse failure is an ordinary
 * outcome rather than an error worth surfacing.
 */
export function loadProfile(store: ProfileStore | null = browserStore()): Profile {
  if (!store) return emptyProfile();
  try {
    const raw = store.getItem(PROFILE_KEY);
    if (!raw) return emptyProfile();
    const parsed = JSON.parse(raw) as Partial<Profile>;
    if (parsed?.version !== MECHANICS_VERSION) return emptyProfile();
    return {
      version: MECHANICS_VERSION,
      counts: { ...parsed.counts },
      games: typeof parsed.games === "number" ? parsed.games : 0,
      // **`dismissed` has to be read back, and for a while it was not.** It
      // was written on every × and dropped on every load, so "never show me
      // this again" meant "until you reload" — and the field's own comment
      // three lines up promised the opposite. Reported from play.
      //
      // Filtered rather than trusted: this is a store a person is explicitly
      // allowed to edit, so the shape is checked like any other input. A
      // missing field stays missing, which is what makes an older profile a
      // profile with nothing dismissed.
      ...(Array.isArray(parsed.dismissed)
        ? { dismissed: parsed.dismissed.filter((id) => typeof id === "string") }
        : {}),
      ...(typeof parsed.hints === "boolean" ? { hints: parsed.hints } : {})
    };
  } catch {
    return emptyProfile();
  }
}

/** Told when something outside a game rewrites the profile. See `resetProfile`. */
type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * Watch for the profile being reset from under you.
 *
 * `useHints` holds the profile in state and reads it once on mount, which is
 * right — it is a session's worth of pacing, not something to re-read per
 * render. The dev control is the one thing that rewrites it from outside, and
 * without this the button's honest label was "reload to see it fresh".
 */
export function onProfileReset(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Forget everything, and say so to anything already holding a copy.
 *
 * The counterweight to a dismissal being permanent: a concept waved away is
 * gone for good, so there has to be one place that gives them all back. **Not
 * a command and not game state** — the profile never reaches the log and
 * `src/engine/rules/` cannot see it, which is the same property that lets a player
 * edit their own `localStorage` without consequence.
 */
export function resetProfile(store: ProfileStore | null = browserStore()): Profile {
  const fresh = emptyProfile();
  saveProfile(fresh, store);
  // A copy, so a listener that unsubscribes while being told does not skip
  // the next one.
  for (const listener of [...listeners]) listener();
  return fresh;
}

/** Write it back. Silent on failure — a full or blocked store is not an error. */
export function saveProfile(
  profile: Profile,
  store: ProfileStore | null = browserStore()
): void {
  try {
    store?.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch {
    // Private browsing, a full quota, a blocked origin. None of them is worth
    // interrupting a game for, and none of them costs anything but a hint.
  }
}

/** A new profile with these mechanics added. Pure: the caller decides to store it. */
export function withMechanics(profile: Profile, met: readonly MechanicId[]): Profile {
  if (met.length === 0) return profile;
  const counts = { ...profile.counts };
  tally(counts, met);
  return { ...profile, counts };
}

/** Has this player ever done it? The question the curriculum actually asks. */
export const hasMet = (profile: Profile, id: MechanicId): boolean =>
  (profile.counts[id] ?? 0) > 0;

/**
 * Is this mechanic still worth teaching?
 *
 * A count rather than a flag, because "has done it once" and "is fluent" are
 * different questions and the curriculum wants the second. The threshold lives
 * with the concept table in 11b; this is the shape it will read.
 */
export const fluentIn = (profile: Profile, id: MechanicId, times = 3): boolean =>
  (profile.counts[id] ?? 0) >= times;

/** A game finished. Counted apart from mechanics: it is how many, not which. */
export const withGamePlayed = (profile: Profile): Profile => ({
  ...profile,
  games: profile.games + 1
});
